import { Fragment, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { T } from "react-translator-component";
import appServices from "../services/app-services/appServices";
import {
  convertUserImage,
  isFinanceRole,
  isGroupChat,
  isGroupLeader,
  isMultiPersonChat,
  isSubLeaderRole
} from "../util/utils";
import { adjustUserInfo } from "../util/MultiMenu";
import Separator from "../components/Separator";
import LoadingModal from "../components/LoadingModal";
import GCBackButton from "../components/GreenCity/GCBackButton";
import MessageModal from "../components/Models/GreenCity/MessageModal";
import TwoButtonModal from "../components/TwoButtonModal";
import { ErrorMessageModal } from "../components/ErrorMessageModal";
import MultiMenu from "../components/MultiMenu";
import { colors } from "../common/colors";
import { Avatar, Box, Typography } from "@mui/material";
import RemoveCircleOutlineIcon from "@mui/icons-material/RemoveCircleOutline";

export default function ChatUsers () {
  const navigate = useNavigate();
  const location = useLocation();
  const { chatId } = location.state;
  const { user } = useSelector((state: any) => state.userAuth);
  const { blocks } = useSelector((state: any) => state.blocks);
  const [chatInfo, setChatInfo] = useState<any>();
  const [groupData, setGroupData] = useState<any>();
  const [members, setMembers] = useState<any[]>([]);
  const [selectedUser, setSelectedUser] = useState<any>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showErrorModal, setShowErrorModal] = useState<boolean>(false);
  const [message, setMessage] = useState<string>("");
  const [errMessageList, setErrMessageList] = useState<string[]>([]);
  const [showMessageModal, setShowMessageModal] = useState<boolean>(false);
  const [showDeleteConfimationModal, setShowDeleteConfimationModal] = useState<boolean>(false);

  const confirmationMessage = `${T("chat_users_confirmation_to_delete")}`;
  const leaveMessage = `${T("chat_users_confirmation_to_leave")}`;

  useEffect(() => {
    apiCall();
  }, []);

  useEffect(() => {
    if (!chatInfo || isGroupChat(chatInfo?.chat_type) || isOneToManyChat()) return

    const blockData = blocks.find(
      (block: any) => block.blocked_type === 'User' && block.blocked_id !== user.id
    )
    if (blockData) navigate(-2)
  }, [chatInfo, blocks, members]);

  const apiCall = async () => {
    try {
      setIsLoading(true);
      const res = await appServices.getChatMessages(chatId)
      const chatData = res.data.chat
      setChatInfo(chatData)
      const chatMembers = chatData.users.map((user: any) => (
        {...user, isInChat: true}
      ))
      setMembers(chatMembers)
      if (chatData.member_group) setGroupData(chatData.member_group)
    } catch (error: any) {
      console.error(error)
      setErrMessageList([error?.response?.data?.message]);
      setShowErrorModal(true)
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteUser = async (targetUser: any) => {
    try {
      setShowDeleteConfimationModal(false)
      setIsLoading(true)
      await appServices.deleteUserChat(chatInfo.id, targetUser?.id, user.auth_token)

      if (targetUser?.id === user.id) {
        navigate(-2);
        return false;
      }

      setMembers(members.map((member: any) => {
        if (member.id === targetUser.id) {
          member.isInChat = false
        }
        return member
      }))

      setMessage(`${T("chat_users_left")}`);
      setShowMessageModal(true);

      setTimeout(() => {
        setShowMessageModal(false);
      }, 3000);
      return true
    } catch (error: any) {
      console.error(error)
      setErrMessageList([error?.response?.data?.message]);
      setShowErrorModal(true)
      return false
    } finally {
      setIsLoading(false)
    }
  };

  const fetchGroupRole = (member: any) => {
    if (!groupData) return ""

    return groupData.member_group_users.find((groupMember: any) => groupMember.id === member.id)?.group_role
  }

  const showRemoveIcon = (member: any) => {
    // チャット作成者は削除できない
    if (member.id === chatInfo?.creator_id) return false
    if (!member?.isInChat) return false

    const isGroupTypeChat = isGroupChat(chatInfo?.chat_type)

    if (
      isGroupTypeChat &&
      groupData &&
      (
        isGroupLeader(groupData, member) ||
        isSubLeaderRole(fetchGroupRole(member)) ||
        isFinanceRole(fetchGroupRole(member))
      )
    ) {
      // グループ全体チャットでは役職者は削除できない
      return false
    }

    if (isGroupTypeChat) {
      const isGroupExecutives =
        groupData &&
        (isGroupLeader(groupData, user) || isSubLeaderRole(fetchGroupRole(user)))

      // グループ全体チャットではリーダーかサブリーダーしかメンバーを削除できない
      if (!isGroupExecutives) return false
    }

    return true
  };

  const handleClickRemoveIcon = (selectedUser: any) => {
    setSelectedUser(selectedUser)
    setShowDeleteConfimationModal(true)
  }

  const handleCloseDeleteConfimationModal = () => {
    setSelectedUser(undefined)
    setShowDeleteConfimationModal(false)
  }

  const handleCloseMessageModal = () => {
    setShowMessageModal(false)
    setMessage("")
  }

  const isOneToManyChat = () => {
    const isInChatUsers = members.filter((member: any) => member.isInChat)
    return isMultiPersonChat(chatInfo?.chat_type, isInChatUsers)
  }

  return (
    <Box>
      <LoadingModal visible={isLoading} />

      <Box style={styles.headerContainer}>
        <div style={styles.goBackBtn}>
          <GCBackButton onClick={() => navigate(-1)} />
        </div>
        <Box sx={styles.titleContainer}>
          <Typography style={styles.title}>{T("chat_users_title")}</Typography>
        </Box>
      </Box>

      <Separator color={colors.farm_gray100} sx={styles.separator} />

      <div style={styles.membersContainer}>
        <Box style={styles.totalCount}>{members.length}人</Box>

        {members.length > 0 && (
          <Box sx={styles.membersBox}>
            {members?.map((member: any, index: number) => (
              <Fragment key={`chat_member_${index}`}>
                <div style={styles.userInfo}>
                  <Avatar sx={styles.userImage} src={convertUserImage(member?.image)} />
                  <Typography style={styles.userName}>
                    {member?.nickname || member?.name}
                  </Typography>

                  {showRemoveIcon(member) && (
                    <RemoveCircleOutlineIcon
                      sx={styles.removeIcon}
                      onClick={() => handleClickRemoveIcon(member)}
                    />
                  )}

                  {/* ・・・メニューアイコン */}
                  <MultiMenu
                    targetUser={adjustUserInfo(member)}
                    menuType={"chat"}
                    isOneToOneChat={!(isGroupChat(chatInfo?.chat_type) || isOneToManyChat())}
                    groupInfo={groupData}
                  />
                </div>
                <Separator color={colors.farm_gray100} sx={styles.separator} />
              </Fragment>
            ))}
          </Box>
        )}
      </div>

      <MessageModal
        open={showMessageModal}
        message={message}
        onClose={() => handleCloseMessageModal()}
      />

      <TwoButtonModal
        showButton={true}
        open={showDeleteConfimationModal}
        message={selectedUser?.id === user.id ? leaveMessage : confirmationMessage}
        onClose={() => handleCloseDeleteConfimationModal()}
        onContinue={() => handleDeleteUser(selectedUser)}
        yesText={selectedUser?.id === user.id ? T("chat_users_leave") : T("chat_users_delete")}
      />

      {/* エラーメッセージ用 */}
      <ErrorMessageModal
        open={showErrorModal}
        messages={errMessageList}
        onClose={() => setShowErrorModal(false)}
      />
    </Box>
  );
};

const styles = {
  goBackBtn: {
    marginLeft: "-10px",
  },

  titleContainer: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    paddingRight: "10px",
  },

  title: {
    fontWeight: "700",
    marginTop: "15px"
  },

  separator: {
    height: "1.5px"
  },

  membersContainer: {
    margin: "0 20px",
  },

  membersBox: {
    marginBottom: "20px"
  },

  userImage: {
    width: 30,
    height: 30,
    mr: 1
  },

  userName: {
    flex: 1,
    lineHeight: "34px"
  },

  removeIcon: {
    color: colors.farm_red600,
    fontSize: "34px",
    cursor: "pointer"
  },

  totalCount: {
    backgroundColor: "#F4F4F4",
    marginTop: 20,
    marginBottom: 20,
    borderRadius: 10,
    padding: "16px 10px 15px",
  },

  headerContainer: {
    padding: "90px 20px 12px",
  },

  userInfo: {
    display: "flex",
    alignItmes: "center",
    padding: "15px 0 5px 10px",
  },
};
